// ./src/utils/api/EventPageService.ts
//
// Pages of an event's public website — the CMS layer. Mirrors the eventsmagic-api
// EventPage SDL. A page has no content of its own; its ordered blocks live in
// page_widget (see PageWidgetService). `where.id` accepts one id or many — a bulk
// update (>1 id) may only change status (publish/unpublish), server-enforced.
// `status` doubles as the publish state (ACTIVE = live, DRAFT = hidden).
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';
import { listAllPages } from './listAllPages';

const FIELDS = `
  id
  eventId
  name
  path
  icon
  lang
  accessLevel
  accessRsvpStatuses
  accessGuestGroupIds
  accessTicketTypeIds
  visibleWhen
  orderNo
  showInNav
  isHome
  seoTitle
  seoDescription
  seoImageUploadedFileId
  settings
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query EventPageList($filter: EventPageFilter, $params: PaginationAndSorting) {
    eventPageList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query EventPageGet($id: ID) {
    eventPageGet(id: $id) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation EventPageCreate($params: EventPageInput) {
    eventPageCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation EventPageUpdate($where: EventPageWhereInput, $params: EventPageInput) {
    eventPageUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation EventPageRemove($where: EventPageWhereInput) {
    eventPageRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

const DUPLICATE_MUTATION = gql`
  mutation EventPageDuplicate($where: EventPageWhereInput, $params: EventPageDuplicateInput) {
    eventPageDuplicate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

export type PageAccessLevel = 'public' | 'guest' | 'conditional';

export interface EventPage {
  id?: string;
  eventId?: string | null;
  name?: string | null;
  path?: string | null;
  icon?: string | null;
  lang?: string | null;
  accessLevel?: PageAccessLevel | string | null;
  accessRsvpStatuses?: string[] | null;
  accessGuestGroupIds?: string[] | null;
  accessTicketTypeIds?: string[] | null;
  /** When it shows: always | before | during | after the event (null = always) — ROADMAP A2e #92. */
  visibleWhen?: string | null;
  orderNo?: number | null;
  showInNav?: boolean | null;
  isHome?: boolean | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoImageUploadedFileId?: string | null;
  settings?: Record<string, unknown> | null;
  status?: number | null;
  rights?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface EventPageInput {
  eventId?: string | null;
  name?: string | null;
  path?: string | null;
  icon?: string | null;
  lang?: string | null;
  accessLevel?: PageAccessLevel | string | null;
  accessRsvpStatuses?: string[] | null;
  accessGuestGroupIds?: string[] | null;
  accessTicketTypeIds?: string[] | null;
  /** When it shows: always | before | during | after the event (null = always) — ROADMAP A2e #92. */
  visibleWhen?: string | null;
  orderNo?: number | null;
  showInNav?: boolean | null;
  isHome?: boolean | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoImageUploadedFileId?: string | null;
  settings?: Record<string, unknown> | null;
  status?: number | null;
}

export class EventPageService {
  static async list(eventId: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { eventId: [eventId], status: statuses },
    });
  }

  /**
   * #62 P6(d) — every page of an event (live and draft), read WHOLE. ⚠ ROADMAP #118: it was one page of 200, so the
   * 201st page was not in the editor's page list nor in the wizard's "what the site already carries".
   */
  static async listForEvent(eventId: string, statuses: number[]): Promise<OpResult> {
    return listAllPages<EventPage>(
      (filter, pagination, sortBy) => ApiService.executeQuery(LIST_QUERY, { filter, params: { pagination, sortBy } }),
      { eventId: [eventId], status: statuses },
    );
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  static async create(params: EventPageInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: EventPageInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }

  /** Copy a page (+ its blocks + host items) into a target event. */
  static async duplicate(id: string, targetEventId: string): Promise<OpResult> {
    return ApiService.executeMutation(DUPLICATE_MUTATION, { where: { id }, params: { eventId: targetEventId } });
  }
}
